import { UserWithAdminInfo } from '@/reducers/admin/v2/allUsersReducerV2'
import { useCallback, useState } from 'react'
import { adminDeleteUser } from '@/actions/admin/adminAllUsersActions'
import { Button, Form, Icon, Input } from 'semantic-ui-react'
import { UnstyledDropdown } from '@/componentsV1/BaseComponents/Dropdown'
import { Modal } from '@/componentsV1/BaseComponents'
import { SearchType } from '@/actions/admin/v2/adminUsersActions'
import { useAppDispatch } from '@/utils/typeHelpers'

export const ConfirmUserDeletion = ({
  user,
}: {
  user: Pick<UserWithAdminInfo, 'id' | 'firstName' | 'lastName' | 'email'>
}) => {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const [confirmUser, setConfirmUser] = useState<string>()
  const [deleting, setDeleting] = useState(false)

  const verifyDeletion = () => {
    return confirmUser === `${user.firstName} ${user.lastName}`
  }

  const validDelete = verifyDeletion()

  const handleDeleteUser = useCallback(async () => {
    setDeleting(true)
    const result = await adminDeleteUser(user.id)(dispatch)
    if (result) {
      setOpen(false)
      setDeleting(false)
    }
  }, [dispatch, user.id])

  return (
    <>
      <Button negative onClick={() => setOpen(true)} floated="right">
        Delete User
      </Button>
      <Modal size="small" open={open}>
        <Modal.Header>
          <Icon name="warning sign" color="yellow" />
          Are you sure you wanted to permanently delete: {user.firstName}{' '}
          {user.lastName}?
        </Modal.Header>
        <Modal.Content>
          You are permanently deleting:
          <ul>
            <li>UID {user.id}</li>
            <li>
              {user.firstName} {user.lastName}
            </li>
            <li>{user.email}</li>
          </ul>
          <Form>
            <Form.Field>
              <Form.Input
                label="To confirm deletion, type the user's full name"
                placeholder={`${user.firstName} ${user.lastName}`}
                onChange={(e, target) => setConfirmUser(target.value)}
                fluid
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button secondary onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            negative
            disabled={!validDelete || deleting}
            onClick={() => handleDeleteUser()}
            loading={deleting}
          >
            Delete this user
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  )
}

export const FilterByBookkeeperIdDropdown = ({
  bookkeeperOptions,
  setBookkeeperId,
  bookkeeperId,
  resetPaginationAndActivePage,
}: {
  bookkeeperOptions: { key: number; text: string; value: number }[]
  setBookkeeperId: (bookkeeperId?: number) => void
  bookkeeperId?: number
  resetPaginationAndActivePage: () => void
}) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <label className="text-sm font-semibold">Filter By Bookkeeper</label>
    <UnstyledDropdown
      onChange={(data) => {
        setBookkeeperId(data)
        resetPaginationAndActivePage()
      }}
      value={bookkeeperId ?? undefined}
      clearable
      search
      options={bookkeeperOptions}
      placeholder="Filter by Bookkeeper"
      selectOnBlur={false}
      fluid
    />
  </div>
)

export const SortByDropdown = ({
  orderBy,
  setOrderBy,
  resetPaginationAndActivePage,
}: {
  orderBy?: string
  setOrderBy: (orderBy?: string) => void
  resetPaginationAndActivePage: () => void
}) => {
  const sortOptions = [
    { key: 'id:asc', text: 'ID (Oldest First)', value: 'id:asc' },
    { key: 'id:desc', text: 'ID (Newest First)', value: 'id:desc' },
    { key: 'firstName:asc', text: 'First Name (A-Z)', value: 'firstName:asc' },
    {
      key: 'firstName:desc',
      text: 'First Name (Z-A)',
      value: 'firstName:desc',
    },
    { key: 'lastName:asc', text: 'Last Name (A-Z)', value: 'lastName:asc' },
    { key: 'lastName:desc', text: 'Last Name (Z-A)', value: 'lastName:desc' },
    { key: 'email:asc', text: 'Email (A-Z)', value: 'email:asc' },
    { key: 'email:desc', text: 'Email (Z-A)', value: 'email:desc' },
    {
      key: 'createdAt:asc',
      text: 'Created Date (Oldest)',
      value: 'createdAt:asc',
    },
    {
      key: 'createdAt:desc',
      text: 'Created Date (Newest)',
      value: 'createdAt:desc',
    },
  ]

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <label className="text-sm font-semibold">Sort By</label>
      <UnstyledDropdown
        onChange={(data) => {
          setOrderBy(data)
          resetPaginationAndActivePage()
        }}
        value={orderBy ?? undefined}
        clearable
        options={sortOptions}
        placeholder="Sort by..."
        selectOnBlur={false}
        fluid
      />
    </div>
  )
}

export const isUserIdSearch = (value: string): boolean => /^\d+$/.test(value)

export const SearchInput = ({
  searchType,
  setSearchType,
  onSearchSubmit,
  defaultValue = '',
}: {
  searchType: SearchType
  setSearchType: (searchType: SearchType) => void
  onSearchSubmit: (search: string) => void
  defaultValue?: string
}) => {
  const options = [
    { key: SearchType.ById, text: 'By user id', value: SearchType.ById },
    {
      key: SearchType.BySearchString,
      text: 'By name or email',
      value: SearchType.BySearchString,
    },
  ]

  const [searchString, setSearchString] = useState<string>(defaultValue)

  return (
    <div style={{ width: '100%' }}>
      <label className="text-sm font-semibold">Search</label>
      <div className="flex items-center gap-2">
        <Input
          type="text"
          placeholder="Search..."
          defaultValue={defaultValue}
          fluid
          className="flex-1"
          onChange={(e) => {
            const value = e.target.value

            // Auto change search value based on input
            setSearchType(
              isUserIdSearch(value)
                ? SearchType.ById
                : SearchType.BySearchString
            )

            setSearchString(value)
          }}
          onKeyPress={(e: { key: string }) => {
            if (e.key === 'Enter') {
              onSearchSubmit(searchString)
            }
          }}
        />
        <UnstyledDropdown
          compact
          options={options}
          defaultValue={searchType}
          value={searchType}
          onChange={(data) => {
            setSearchType(data)
          }}
        />
        <Button
          type="submit"
          onClick={() => {
            onSearchSubmit(searchString)
          }}
        >
          Search
        </Button>
      </div>
    </div>
  )
}
